/* Recently viewed rail — Downsized Designs (added 2026-10-19).
 *
 * Records the handle of each product page viewed in this browser's
 * localStorage (most recent first, duplicates removed, capped at 12).
 * Handles only — no personalization text and no shopper data is stored
 * or sent anywhere; the list never leaves this device.
 *
 * Any <recently-viewed> section on the page then renders the stored
 * products (at most 8, excluding the product currently being viewed)
 * from Shopify's built-in /products/<handle>.js data route, so prices
 * and availability are always current. Products that fail to load
 * (moved to Draft, deleted) are skipped silently. The section stays
 * hidden until at least 2 products can be shown.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ddRecentlyViewed';
  var MAX_STORED = 12;
  var MAX_SHOWN = 8;
  var MIN_TO_SHOW = 2;

  function readList() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) return [];
      return list.filter(function (h) {
        return typeof h === 'string' && h.length > 0;
      });
    } catch (e) {
      return [];
    }
  }

  function writeList(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_STORED)));
    } catch (e) {
      /* storage unavailable (private mode, disabled) — rail simply never shows */
    }
  }

  function clearList() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  /* Handle of the product currently being viewed, injected by layout/theme.liquid
     on product pages only; null everywhere else. */
  var currentHandle =
    typeof window.DDRecentlyViewedCurrent === 'string' && window.DDRecentlyViewedCurrent
      ? window.DDRecentlyViewedCurrent
      : null;

  /* 1. Record the current product view. */
  if (currentHandle) {
    var recorded = readList().filter(function (h) {
      return h !== currentHandle;
    });
    recorded.unshift(currentHandle);
    writeList(recorded);
  }

  /* 2. Render helpers. */
  function rootPath() {
    if (window.Shopify && Shopify.routes && Shopify.routes.root) return Shopify.routes.root;
    return '/';
  }

  function money(cents) {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function imageUrl(product) {
    var img = product.featured_image;
    if (!img) return '';
    if (img.indexOf('//') === 0) img = 'https:' + img;
    return img + (img.indexOf('?') === -1 ? '?' : '&') + 'width=533';
  }

  function cardHtml(product, opts) {
    var url = rootPath() + 'products/' + encodeURIComponent(product.handle);
    var img = imageUrl(product);
    var ratio = opts.ratio;
    var ratioPercent = ratio === 'portrait' ? 125 : 100;
    var onSale = product.compare_at_price && product.compare_at_price > product.price;

    var badge = '';
    if (!product.available) {
      badge =
        '<span class="badge badge--bottom-left color-' + esc(opts.soldOutScheme) + '">Sold out</span>';
    } else if (onSale) {
      badge = '<span class="badge badge--bottom-left color-' + esc(opts.saleScheme) + '">Sale</span>';
    }

    var priceInner = '';
    if (product.price_varies) {
      priceInner =
        '<span class="price-item price-item--regular">From ' + money(product.price_min) + '</span>';
    } else if (onSale) {
      priceInner =
        '<span class="price-item price-item--sale price-item--last">' + money(product.price) + '</span> ' +
        '<span><s class="price-item price-item--regular">' + money(product.compare_at_price) + '</s></span>';
    } else {
      priceInner = '<span class="price-item price-item--regular">' + money(product.price) + '</span>';
    }
    var priceHtml =
      '<div class="price' + (onSale ? ' price--on-sale' : '') + '">' +
      '<div class="price__container">' + priceInner + '</div></div>';

    var mediaHtml = img
      ? '<div class="card__media"><div class="media media--transparent media--hover-effect">' +
        '<img src="' + esc(img) + '" alt="' + esc(product.title) + '" width="533" height="533" loading="lazy">' +
        '</div></div>'
      : '';

    return (
      '<li class="grid__item">' +
      '<div class="card-wrapper product-card-wrapper underline-links-hover">' +
      '<div class="card card--standard card--media" style="--ratio-percent: ' + ratioPercent + '%;">' +
      '<div class="card__inner color-' + esc(opts.cardScheme) + ' gradient ratio" style="--ratio-percent: ' + ratioPercent + '%;">' +
      mediaHtml +
      '<div class="card__content"><div class="card__information">' +
      '<h3 class="card__heading"><a href="' + esc(url) + '" class="full-unstyled-link" aria-hidden="true" tabindex="-1">' + esc(product.title) + '</a></h3>' +
      '</div>' +
      (badge ? '<div class="card__badge bottom left">' + badge + '</div>' : '') +
      '</div>' +
      '</div>' +
      '<div class="card__content"><div class="card__information">' +
      '<h3 class="card__heading h5"><a href="' + esc(url) + '" class="full-unstyled-link">' + esc(product.title) + '</a></h3>' +
      priceHtml +
      '</div></div>' +
      '</div>' +
      '</div>' +
      '</li>'
    );
  }

  /* 3. Render each rail section on the page. */
  function renderRail(el) {
    if (el.dataset.rvInit) return;
    el.dataset.rvInit = '1';

    var clearBtn = el.querySelector('[data-rv-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        clearList();
        document.querySelectorAll('recently-viewed').forEach(function (node) {
          var list = node.querySelector('[data-rv-list]');
          if (list) list.innerHTML = '';
          node.hidden = true;
        });
        if (window.ddAnnounce) window.ddAnnounce('Recently viewed cleared.');
      });
    }

    var handles = readList()
      .filter(function (h) {
        return h !== currentHandle;
      })
      .slice(0, MAX_SHOWN);
    if (handles.length < MIN_TO_SHOW) return; /* stays hidden */

    var opts = {
      ratio: el.getAttribute('data-ratio') || 'square',
      cardScheme: el.getAttribute('data-card-scheme') || 'scheme-1',
      saleScheme: el.getAttribute('data-sale-badge-scheme') || 'scheme-1',
      soldOutScheme: el.getAttribute('data-sold-out-badge-scheme') || 'scheme-1'
    };

    Promise.all(
      handles.map(function (h) {
        return fetch(rootPath() + 'products/' + encodeURIComponent(h) + '.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        })
          .then(function (r) {
            if (!r.ok) throw new Error('skip');
            return r.json();
          })
          .catch(function () {
            return null; /* Draft/deleted/unreachable — skip silently */
          });
      })
    ).then(function (products) {
      var good = products.filter(function (p) {
        return p && p.handle && p.title;
      });
      if (good.length < MIN_TO_SHOW) return; /* stays hidden */
      var list = el.querySelector('[data-rv-list]');
      if (!list) return;
      list.innerHTML = good
        .map(function (p) {
          return cardHtml(p, opts);
        })
        .join('');
      if (opts.ratio === 'adapt') {
        list.querySelectorAll('.card').forEach(function (card) {
          var img = card.querySelector('img');
          if (!img) return;
          var apply = function () {
            if (img.naturalWidth > 0) {
              var pct = Math.round((img.naturalHeight / img.naturalWidth) * 1000) / 10;
              card.style.setProperty('--ratio-percent', pct + '%');
              card.querySelectorAll('[style]').forEach(function (n) {
                n.style.setProperty('--ratio-percent', pct + '%');
              });
            }
          };
          if (img.complete && img.naturalWidth > 0) apply();
          else img.addEventListener('load', apply);
        });
      }
      el.hidden = false;
    });
  }

  function init() {
    document.querySelectorAll('recently-viewed').forEach(renderRail);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
